import { useCallback, useEffect, useState } from 'react'
import { listOpenTasks, type OpenTask, type Status, type TaskRow } from '../lib/api'
import {
  adminListEmployees,
  adminListTasks,
  type AdminEmployee,
  type AdminTask,
} from '../lib/adminApi'
import { bucketBy, formatDate, relativeDue } from '../lib/grouping'
import type { TaskRef } from '../lib/meeting'
import AdminTasks, { type TaskPrefill } from './AdminTasks'
import TaskCard from './TaskCard'

interface Props {
  token: string
  isAdmin: boolean
  meId: string | null
  /** App's own list for the signed-in person — their cards act through App. */
  tasks: TaskRow[]
  busy: Set<string>
  onStatus: (assignmentId: string, status: Status) => void
  onNote: (assignmentId: string, note: string) => void
  onTasksChanged: () => void
  /** Admin only: "Create a task" from IDS lands here, form already filled. */
  prefill: TaskPrefill | null
  onPrefillUsed: () => void
  onFormClosed: () => void
  /**
   * "Create an issue" on a to-do — a linked issue, made without leaving the
   * meeting. Admins get it on every to-do; everyone else on their own.
   */
  onCreateIssue: (task: TaskRef) => void
  /** Bumped by Meeting when the presenter acts, so the list re-fetches. */
  refreshKey: number
}

/**
 * The To-Dos section: every open task in the shop, reviewed together.
 *
 * An admin gets the real admin list (create, edit, archive, their own status
 * buttons) in its compact desktop form. Everyone else gets `list_open_tasks`
 * — the same tasks minus people's private notes — and the rows that are
 * THEIR OWN assignment render as the real TaskCard from App's list, so a
 * status tap in the meeting goes through exactly the same path as on the
 * Tasks tab.
 */
export default function MeetingTodos({
  token,
  isAdmin,
  meId,
  tasks,
  busy,
  onStatus,
  onNote,
  onTasksChanged,
  prefill,
  onPrefillUsed,
  onFormClosed,
  onCreateIssue,
  refreshKey,
}: Props) {
  const [adminTasks, setAdminTasks] = useState<AdminTask[] | null>(null)
  const [people, setPeople] = useState<AdminEmployee[]>([])
  const [open, setOpen] = useState<OpenTask[] | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      if (isAdmin) {
        const [t, p] = await Promise.all([adminListTasks(token), adminListEmployees(token)])
        setAdminTasks(t)
        setPeople(p)
      } else {
        setOpen(await listOpenTasks(token))
      }
      setProblem(null)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
      if (isAdmin) setAdminTasks([])
      else setOpen([])
    }
  }, [token, isAdmin])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  if (isAdmin) {
    if (adminTasks === null) return <p className="muted">Loading…</p>
    return (
      <>
        {problem && <p className="banner">{problem}</p>}
        <AdminTasks
          token={token}
          tasks={adminTasks}
          people={people}
          meId={meId}
          prefill={prefill}
          onPrefillUsed={onPrefillUsed}
          onChanged={() => {
            void load()
            onTasksChanged()
          }}
          onProblem={setProblem}
          onFormClosed={onFormClosed}
          onCreateIssue={onCreateIssue}
          compact
        />
      </>
    )
  }

  if (open === null) return <p className="muted">Loading…</p>

  const buckets = bucketBy(
    open,
    (t) => t.days_until,
    () => false,
  )

  const mineFor = (t: OpenTask) => tasks.find((row) => row.task_id === t.task_id)

  const row = (t: OpenTask) => {
    const mine = mineFor(t)
    if (mine) {
      return (
        <TaskCard
          key={t.task_id}
          task={mine}
          token={token}
          busy={busy.has(mine.assignment_id)}
          onStatus={onStatus}
          onNote={onNote}
          onLinksChanged={onTasksChanged}
          onCreateIssue={onCreateIssue}
        />
      )
    }
    const overdue = t.days_until < 0
    return (
      <article
        key={t.task_id}
        className={'card task card--row' + (overdue ? ' card--overdue' : '')}
      >
        <div className="card__head">
          <h3 className="card__title">{t.title}</h3>
          {t.priority !== 'Medium' && (
            <span className={`chip chip--${t.priority.toLowerCase()}`}>{t.priority}</span>
          )}
        </div>
        <p className="card__due">
          <span className="card__date">Due {formatDate(t.due_date)}</span>
          <span className={overdue ? 'card__rel card__rel--late' : 'card__rel'}>
            {' · '}
            {relativeDue(t.days_until)}
          </span>
          <span className="card__prog">
            {' · '}
            {t.done_count}/{t.total_count} done
          </span>
          {t.raised_issue_count > 0 && (
            <span className="card__prog">
              {' · '}
              {t.raised_issue_count === 1 ? '1 issue raised' : `${t.raised_issue_count} issues raised`}
            </span>
          )}
        </p>
        {t.issue_title && (
          <p className="card__brief card__brief--from">From the issue “{t.issue_title}”</p>
        )}
        {t.headline_body && (
          <p className="card__brief card__brief--from">From the headline “{t.headline_body}”</p>
        )}
        <div className="who">
          {t.assignees.map((a) => (
            <span
              key={a.employee_id}
              className={
                'who__chip who__chip--' +
                (a.status === 'Completed' ? 'done' : a.status === 'In Progress' ? 'working' : 'todo')
              }
            >
              {a.full_name}
            </span>
          ))}
        </div>
      </article>
    )
  }

  return (
    <section>
      {problem && <p className="banner">{problem}</p>}

      {open.length === 0 && (
        <div className="panel panel--flat">
          <p className="muted">No open tasks. Everything is clear.</p>
        </div>
      )}

      {buckets.map((bucket) => (
        <section key={bucket.key} className="group">
          <h2 className={`group__label group__label--${bucket.key}`}>
            {bucket.key === 'later' ? 'Tasks' : bucket.label}
            <span className="group__count">{bucket.items.length}</span>
          </h2>
          {bucket.items.map(row)}
        </section>
      ))}
    </section>
  )
}
